import { TopicField } from '../../../origami-api/utils/topic-utils';
import { FieldType } from '../../../origami-api/constants';
import { StringHash } from '@app/shared/utils/utils-types';
import { Observable, of } from 'rxjs';

export class SettingValue<T> {
  constructor(
    private dbName: string,
    private defaultValue: string | boolean | number,
    private mainStore: StringHash<TopicField>,
    private defaultStore: StringHash<TopicField>,
    private saveData: (field: TopicField) => Observable<boolean>,
    private setterConverter: (value: T) => string = null,
    private getterConverter: (value: string) => T = null,
  ) {}

  setAndSave(value: T): Observable<boolean> {
    this.set(value);
    return this.save();
  }

  save(): Observable<boolean> {
    const field = this.getDbValue();
    return field ? this.saveData(field) : of(false);
  }

  set(value: T) {
    const updatedValue = this.setterConverter
      ? this.setterConverter(<any>value)
      : this.defaultSetterConverter(<any>value);
    const field = this.createTopicField(updatedValue);
    (<any>field).Updated = true;

    this.mainStore[this.dbName] = field;
  }

  get(onlyCurrentValue: boolean = false): T {
    const field = this.getField(false, onlyCurrentValue);
    if (field) {
      return this.getterConverter
        ? this.getterConverter(field.Fieldvalue)
        : <any>this.defaultGetterConverter(field.Fieldvalue);
    }
    return this.getterConverter && typeof this.defaultValue === 'string'
      ? this.getterConverter(this.defaultValue)
      : <any>this.defaultValue;
  }

  getFieldId(): number {
    const field = this.getField();
    return field?.FieldId;
  }

  getDbValue(returnOnlyDirty: boolean = true): TopicField {
    let field = this.getFieldInStore(this.mainStore, returnOnlyDirty);

    if (field) {
      field = Object.assign({}, field);
      delete (<any>field).Updated;
    } else if (!field && !returnOnlyDirty) {
      field = this.createTopicField(this.defaultSetterConverter(this.defaultValue));
    }

    return field;
  }

  getMainStoreField(fieldName: string): TopicField {
    return this.mainStore[fieldName];
  }

  private defaultSetterConverter(value: string | number | boolean): string {
    return value == null ? null : typeof value === 'boolean' ? '' + +value : '' + value;
  }

  private defaultGetterConverter(value: string): string | number | boolean {
    return typeof this.defaultValue === 'boolean' ? !!+value : typeof this.defaultValue === 'number' ? +value : value;
  }

  private createTopicField(value: string): TopicField {
    return <TopicField>{
      FieldName: this.dbName,
      FieldType:
        typeof this.defaultValue === 'boolean' || typeof this.defaultValue === 'number'
          ? FieldType.Number
          : FieldType.Text,
      Fieldvalue: value,
      FieldId: this.getFieldId(),
    };
  }

  private getField(returnOnlyDirty: boolean = false, ignoreDefaultStore: boolean = false): TopicField {
    let value = this.getFieldInStore(this.mainStore, returnOnlyDirty);
    if (!value && !ignoreDefaultStore) {
      value = this.getFieldInStore(this.defaultStore, returnOnlyDirty);
    }
    return value;
  }

  private getFieldInStore(store: StringHash<TopicField>, returnOnlyDirty: boolean = false): TopicField {
    let field = store ? store[this.dbName] : null;
    return field && (!returnOnlyDirty || (<any>field).Updated) ? field : null;
  }
}
